import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import RankingSidebar from '@/components/feature/RankingSidebar';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, rankings, latestUpdates, recommended, allComics } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  const categoryComics = useMemo(() => {
    const byCategory = allComics.filter((c) => c.category === currentCategory.name);
    const others = allComics.filter((c) => c.category !== currentCategory.name);
    const combined = [...byCategory, ...others].filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i);

    switch (sortBy) {
      case 'latest':
        return [...combined].sort((a, b) => {
          const aTime = (a as any).updateTime || '';
          const bTime = (b as any).updateTime || '';
          return bTime.localeCompare(aTime);
        });
      case 'rating':
        return [...combined].sort((a, b) => ((b as any).rating || 0) - ((a as any).rating || 0));
      default:
        return [...combined].sort((a, b) => {
          const aViews = parseInt(((a as any).views || '0').replace(/[万]/g, '0000').replace(/[^0-9]/g, '')) || 0;
          const bViews = parseInt(((b as any).views || '0').replace(/[万]/g, '0000').replace(/[^0-9]/g, '')) || 0;
          return bViews - aViews;
        });
    }
  }, [currentCategory.name, sortBy]);

  const totalPages = Math.max(1, Math.ceil(categoryComics.length / pageSize));
  const paginatedComics = categoryComics.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-6">
          <div className="max-w-[1280px] mx-auto">
            <Breadcrumb items={[
              { label: '首页', path: '/' },
              { label: '分类浏览' },
              { label: currentCategory.name },
            ]} />

            <div className="mt-6">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-8 mt-8">
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">{currentCategory.name}漫画</h1>
                    <p className="text-foreground-500 text-sm mt-1">{currentCategory.description}</p>
                  </div>
                  <div className="flex items-center gap-2 bg-background-100 rounded-lg p-1">
                    {[
                      { key: 'popular', label: '最热' },
                      { key: 'latest', label: '最新' },
                      { key: 'rating', label: '评分' },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => { setSortBy(opt.key as any); setCurrentPage(1); }}
                        className={`px-3 py-1.5 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
                          sortBy === opt.key
                            ? 'bg-background-50 text-foreground-950 font-medium'
                            : 'text-foreground-500 hover:text-foreground-700'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-product-shop="">
                  {paginatedComics.map((comic) => (
                    <ComicCard
                      key={comic.id}
                      id={comic.id}
                      title={comic.title}
                      cover={comic.cover}
                      category={comic.category}
                      rating={(comic as any).rating}
                      status={(comic as any).status}
                      newChapter={(comic as any).newChapter}
                    />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-200 text-foreground-500 hover:text-foreground-950 hover:border-foreground-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                          page === currentPage
                            ? 'bg-primary-500 text-background-50'
                            : 'border border-background-200 text-foreground-500 hover:text-foreground-950 hover:border-foreground-300'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-200 text-foreground-500 hover:text-foreground-950 hover:border-foreground-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </div>

              <div className="w-full lg:w-[300px] flex-shrink-0">
                <RankingSidebar rankings={[...rankings.weekly, ...rankings.monthly].filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i)} />
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}